import { Badge } from '@amore/ui/components/badge'
import { Field } from '@amore/ui/components/field'
import { FormLayout } from '@amore/ui/components/form-layout'
import { Select, SelectItem } from '@amore/ui/components/select'
import { TimePicker } from '@amore/ui/components/time-picker'
import { Clock3, UserRound } from 'lucide-react'
import { useRef, useState } from 'react'
import { supplierStatusLabels, supplierStatusTone } from '@/lib/labels'
import { usePrototype } from '@/mock/store'
import { DetailSheet } from '@/patterns/dialog'
import { useToast } from '@/shell/toaster'

const softControl = 'h-10 border-transparent bg-muted/60 hover:bg-muted'

const timeLabels = {
  open: 'Vybrat čas příjezdu',
  title: 'Příjezd oddávajícího',
  close: 'Zavřít výběr času',
  hours: 'Hodiny',
  minutes: 'Minuty',
  clear: 'Bez času',
  confirm: 'Potvrdit čas',
  format: '24hodinový formát · HH:mm',
}

export function CeremonyOfficiantSheet({ onClose }: { onClose: () => void }) {
  const { data, canEdit, update } = usePrototype()
  const { showToast } = useToast()
  const [supplierId, setSupplierId] = useState(data.ceremony.officiantSupplierId)
  const [arrival, setArrival] = useState(data.ceremony.officiantArrival ?? '')
  const [arrivalError, setArrivalError] = useState<string>()
  const formRef = useRef<HTMLFormElement>(null)
  const arrivalRef = useRef<HTMLInputElement>(null)
  const suppliers = data.suppliers.filter(
    (supplier) => supplier.status !== 'cancelled' || supplier.id === supplierId,
  )
  const selected = suppliers.find((supplier) => supplier.id === supplierId)
  const dirty =
    canEdit &&
    (supplierId !== data.ceremony.officiantSupplierId ||
      arrival !== (data.ceremony.officiantArrival ?? ''))

  const save = () => {
    if (!canEdit) return
    if (arrival && !/^([01]\d|2[0-3]):[0-5]\d$/.test(arrival)) {
      setArrivalError('Zadejte čas ve formátu HH:mm, například 13:30.')
      arrivalRef.current?.focus()
      return
    }
    update((current) => ({
      ...current,
      ceremony: {
        ...current.ceremony,
        officiantSupplierId: supplierId,
        officiantArrival: arrival || null,
      },
    }))
    showToast({ title: 'Oddávající uložen', tone: 'success' })
    onClose()
  }

  return (
    <DetailSheet
      open
      eyebrow="Lidé a role"
      title="Oddávající"
      dirty={dirty}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      primaryAction={
        canEdit ? { label: 'Uložit', onClick: () => formRef.current?.requestSubmit() } : undefined
      }
    >
      <FormLayout
        ref={formRef}
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          save()
        }}
        className="divide-y divide-border space-y-0 [&>*]:py-7 [&>*:first-child]:pt-1 [&>*:last-child]:pb-1"
      >
        <Field
          controlId="ceremony-officiant"
          label={
            <span className="flex items-center gap-2 text-sm font-normal text-muted-foreground">
              <UserRound className="size-4 shrink-0" aria-hidden="true" />
              Oddávající
            </span>
          }
          description={
            selected ? (
              <span className="flex flex-wrap items-center gap-2">
                Stav v Dodavatelích{' '}
                <Badge tone={supplierStatusTone(selected.status)}>
                  {supplierStatusLabels[selected.status]}
                </Badge>
              </span>
            ) : undefined
          }
        >
          <Select
            name="officiantSupplierId"
            disabled={!canEdit}
            variant="soft"
            className={softControl}
            value={supplierId ?? 'none'}
            onValueChange={(value) => setSupplierId(value === 'none' ? null : value)}
          >
            <SelectItem value="none">Zatím nevybráno</SelectItem>
            {supplierId && !selected ? (
              <SelectItem value={supplierId}>Dříve vybraný dodavatel</SelectItem>
            ) : null}
            {suppliers.map((supplier) => (
              <SelectItem key={supplier.id} value={supplier.id}>
                {supplier.name}
              </SelectItem>
            ))}
          </Select>
        </Field>
        <Field
          controlId="ceremony-officiant-arrival"
          error={arrivalError}
          label={
            <span className="flex items-center gap-2 text-sm font-normal text-muted-foreground">
              <Clock3 className="size-4 shrink-0" aria-hidden="true" />
              Příjezd
            </span>
          }
        >
          <TimePicker
            ref={arrivalRef}
            name="officiantArrival"
            readOnly={!canEdit}
            variant="soft"
            className={softControl}
            value={arrival}
            labels={timeLabels}
            placeholder="Zatím neurčený"
            onValueChange={(value) => {
              setArrival(value)
              setArrivalError(undefined)
            }}
          />
        </Field>
      </FormLayout>
    </DetailSheet>
  )
}
